document.addEventListener('DOMContentLoaded', () => {
    // Configuration constants
    const CONFIG = {
        // Prices come from the product's variants (set by name-builder.liquid) so the page
        // matches Shopify and Google Shopping; the literals are only a fallback.
        BASE_PRICE: Number(window.butterflyUnframedPrice) || 59.00,
        FRAMED_PRICE: Number(window.butterflyFramedPrice) || 99.00,
        MAX_LETTER_VARIATIONS: 9,  // Check up to letter-X9.jpg (missing images handled gracefully via preload)
        MAX_NAME_LENGTH: 10,
        ANIMATION_DELAY_MS: 100,
        SAMPLE_NAME: 'MARGARET'  // Shown on first visit so the preview is never empty
    };

    // Validate image URL is from Shopify CDN (protocol-relative or https)
    const isValidImageUrl = (url) => typeof url === 'string' && url.length > 0 &&
        (url.includes('cdn.shopify.com/') || url.includes('.myshopify.com/'));

    // DOM Elements
    const nameInput = document.getElementById('NameInput');
    const generateBtn = document.getElementById('GenerateBtn');
    const nameDisplay = document.getElementById('NameDisplayArea');
    const addToCartBtn = document.getElementById('AddToCartBtn');
    const shareDesignBtn = document.getElementById('ShareDesignBtn');
    const shuffleBtn = document.getElementById('shuffleBtn');
    const clickInstruction = document.getElementById('clickInstruction');
    const previewNotice = document.getElementById('previewNotice');
    const displayPriceEl = document.getElementById('displayPrice');
    const framingOptions = document.querySelectorAll('input[name="framing"]');
    const shopFrame = document.getElementById('shopFrame');

    // Sticky mobile add-to-cart bar (mobile only; forwards to the real AddToCartBtn)
    const mobileCta = document.getElementById('nb-mobile-cta');
    const mobileAddToCartBtn = document.getElementById('mobileAddToCartBtn');
    const mobilePriceEl = document.getElementById('mobileDisplayPrice');

    // Inline letter selector elements
    const altLetterContainer = document.getElementById('altLetterContainer');
    const altLetterGrid = document.getElementById('altLetterGrid');
    const currentLetterSpan = document.getElementById('currentLetterSpan');
    const closeAltPanelBtn = document.getElementById('closeAltPanel');

    // Framing option elements
    const optFramed = document.getElementById('opt-framed');
    const optUnframed = document.getElementById('opt-unframed');

    // Guard: Check required elements exist
    if (!nameInput || !generateBtn || !nameDisplay) {
        console.warn('Name Builder: Required elements not found.');
        return;
    }

    // Track letter divs for updating
    let currentLetterDivs = [];
    let currentDesignImages = {};
    let nameBuilderStarted = false; // Track first interaction for GA4
    let skipAutoShuffle = false; // Skip auto-shuffle when loading specific variations from URL
    let isSampleRender = false; // True while rendering the pre-filled sample name (no analytics)
    // Save/Share unlocks only once a person has made a design: typed a name, or opened
    // a shared link or saved design. The pre-filled sample alone never shows it.
    // Since MARGARET was pre-filled (2026-10-19), form-filling bots found the share
    // form on page load and used it to send our design email to strangers.
    let shareUnlocked = false;

    // Persistent variant assignments survive across keystroke regenerations
    // Key: "CHAR_POSITION" → URL. Cleared on Shuffle button or full name change.
    let assignedVariants = {};

    // Cache of validated (existing) variant URLs per letter character
    const validatedVariations = {};

    // ========================================
    // GA4 ENHANCED FUNNEL TRACKING
    // ========================================
    const trackGA4Event = (eventName, params) => {
        if (typeof gtag === 'function') {
            gtag('event', eventName, params);
        }
    };

    const trackLetterVariation = (letter, position, variation) => {
        trackGA4Event('letter_variation_selected', {
            event_category: 'Name Builder',
            letter: letter,
            position: position + 1,
            variation: variation
        });
    };

    const trackShuffle = (nameLength) => {
        trackGA4Event('design_shuffled', {
            event_category: 'Name Builder',
            name_length: nameLength
        });
    };

    const trackFramingSelected = (isFramed) => {
        trackGA4Event('framing_selected', {
            event_category: 'Name Builder',
            frame_type: isFramed ? 'framed' : 'unframed',
            price: isFramed ? CONFIG.FRAMED_PRICE : CONFIG.BASE_PRICE
        });
    };

    // Get available letter image variations for a letter
    const getAvailableLetterVariations = (char) => {
        const lowerChar = char.toLowerCase();
        const variations = [];

        if (!window.butterflyAssetUrlPattern) return variations;

        // Primary image
        const primaryUrl = window.butterflyAssetUrlPattern.replace('letter-CHAR', `letter-${lowerChar}`);
        variations.push(primaryUrl);

        // Variations (letter-a1.jpg, letter-a2.jpg, etc.)
        for (let i = 1; i <= CONFIG.MAX_LETTER_VARIATIONS; i++) {
            const variationUrl = window.butterflyAssetUrlPattern.replace('letter-CHAR', `letter-${lowerChar}${i}`);
            variations.push(variationUrl);
        }

        return variations;
    };

    // Probe and cache which variant images actually exist on CDN for a letter
    const probeVariations = (letter) => {
        const key = letter.toUpperCase();
        if (validatedVariations[key]) {
            return Promise.resolve(validatedVariations[key]);
        }
        const urls = getAvailableLetterVariations(letter);
        return Promise.all(urls.map(url => new Promise(resolve => {
            const img = new Image();
            img.onload = () => resolve(url);
            img.onerror = () => resolve(null);
            img.src = url;
        }))).then(results => {
            const valid = results.filter(url => url !== null);
            validatedVariations[key] = valid;
            return valid;
        });
    };

    // Get letter image URL
    const getLetterImage = (char, specificUrl = null) => {
        const upperChar = char.toUpperCase();
        const lowerChar = char.toLowerCase();

        if (lowerChar.match(/[a-z0-9]/)) {
            if (specificUrl) {
                currentDesignImages[upperChar] = specificUrl;
                return specificUrl;
            }

            if (currentDesignImages[upperChar]) {
                return currentDesignImages[upperChar];
            }

            if (window.butterflyAssetUrlPattern) {
                const assetUrl = window.butterflyAssetUrlPattern.replace('letter-CHAR', `letter-${lowerChar}`);
                currentDesignImages[upperChar] = assetUrl;
                return assetUrl;
            }

            return `https://via.placeholder.com/200x200/065f46/ffffff?text=${char.toUpperCase()}`;
        }
        return null;
    };

    // Format letters for order with position-based labels (L1:E|L2:M2|L3:M|L4:A)
    const formatLettersForOrder = () => {
        const name = nameInput.value.trim().toUpperCase();
        const letters = name.split('').filter(c => c.match(/[A-Z0-9]/));

        return letters.map((letter, index) => {
            const imageUrl = currentDesignImages[`${letter}_${index}`] || currentDesignImages[letter];
            // Extract variant index from URL (letter-a1.jpg → 1, letter-a2.jpg → 2, etc.)
            const match = imageUrl && imageUrl.match(/letter-[a-z0-9](\d+)\.jpg/);
            const varSuffix = match ? match[1] : '';
            return `L${index + 1}:${letter}${varSuffix}`;
        }).join('|');
    };

    // Show success popup when item added to cart
    const showCartSuccessPopup = () => {
        // Remove existing popup if any
        const existingPopup = document.getElementById('cartSuccessPopup');
        if (existingPopup) existingPopup.remove();

        // Create popup (centered on page)
        const popup = document.createElement('div');
        popup.id = 'cartSuccessPopup';
        popup.setAttribute('role', 'alert');
        popup.setAttribute('aria-live', 'assertive');
        popup.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:9999;background:#fff;border-radius:16px;box-shadow:0 25px 50px -12px rgba(0,0,0,0.4);border:1px solid #e7e5e4;padding:24px;display:flex;flex-direction:column;align-items:center;gap:16px;text-align:center;max-width:90vw;width:340px;';
        popup.innerHTML = `
            <button id="closeCartPopup" style="position:absolute;top:12px;right:12px;padding:4px;color:#a8a29e;background:none;border:none;cursor:pointer;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width:20px;height:20px;" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
            </button>
            <div style="width:64px;height:64px;background:#dcfce7;border-radius:50%;display:flex;align-items:center;justify-content:center;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width:32px;height:32px;color:#16a34a;" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
            </div>
            <div>
                <p style="font-weight:700;font-size:18px;color:#1c1917;margin:0;">Added to Cart!</p>
                <p style="font-size:14px;color:#78716c;margin:8px 0 0 0;">Your custom name art is ready for checkout.</p>
            </div>
            <div style="display:flex;gap:12px;width:100%;margin-top:8px;">
                <button id="continueShoppingBtn" style="flex:1;padding:12px 16px;background:#fff;color:#1c1917;font-size:14px;font-weight:600;border-radius:8px;border:1px solid #d6d3d1;cursor:pointer;">
                    Continue Shopping
                </button>
                <a href="/cart" style="flex:1;padding:12px 16px;background:#065f46;color:#fff;font-size:14px;font-weight:700;border-radius:8px;text-decoration:none;text-align:center;">
                    View Cart
                </a>
            </div>
        `;

        // Add backdrop overlay
        const backdrop = document.createElement('div');
        backdrop.id = 'cartPopupBackdrop';
        backdrop.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);z-index:9998;';
        backdrop.addEventListener('click', () => { popup.remove(); backdrop.remove(); });
        document.body.appendChild(backdrop);
        document.body.appendChild(popup);

        // Close handlers
        const closePopup = () => {
            popup.remove();
            document.getElementById('cartPopupBackdrop')?.remove();
        };
        popup.querySelector('#closeCartPopup')?.addEventListener('click', closePopup);
        popup.querySelector('#continueShoppingBtn')?.addEventListener('click', closePopup);
    };

    // Calculate and update price
    const calculatePrice = () => {
        const name = nameInput.value.trim();
        if (!name) return 0;

        const letterCount = name.replace(/[^a-z0-9]/gi, '').length;
        if (letterCount === 0) return 0;

        // Get selected framing option
        const selectedFraming = document.querySelector('input[name="framing"]:checked');
        const isFramed = selectedFraming && selectedFraming.value === 'framed';
        const totalPrice = isFramed ? CONFIG.FRAMED_PRICE : CONFIG.BASE_PRICE;

        // Update price display
        if (displayPriceEl) {
            displayPriceEl.textContent = `$${totalPrice.toFixed(2)}`;
        }
        if (mobilePriceEl) {
            mobilePriceEl.textContent = `$${totalPrice.toFixed(2)}`;
        }

        return totalPrice;
    };

    // Update framing option visual states and frame preview
    const updateFramingStyles = () => {
        const selectedFraming = document.querySelector('input[name="framing"]:checked');
        const isFramed = selectedFraming && selectedFraming.value === 'framed';

        // Update option card styles
        if (optFramed && optUnframed) {
            const framedCircle = optFramed.querySelector('.check-icon');
            const framedCheck = framedCircle?.querySelector('svg');
            const unframedCircle = optUnframed.querySelector('.check-icon');
            const unframedCheck = unframedCircle?.querySelector('svg');

            if (isFramed) {
                optFramed.classList.add('border-emerald-900', 'bg-emerald-50/30');
                optFramed.classList.remove('border-stone-200');
                framedCircle?.classList.add('bg-emerald-700');
                framedCircle?.classList.remove('bg-white', 'border-2', 'border-stone-300');
                framedCheck?.classList.remove('hidden');

                optUnframed.classList.remove('border-emerald-900', 'bg-emerald-50/30');
                optUnframed.classList.add('border-stone-200');
                unframedCircle?.classList.remove('bg-emerald-700');
                unframedCircle?.classList.add('bg-white', 'border-2', 'border-stone-300');
                unframedCheck?.classList.add('hidden');
            } else {
                optUnframed.classList.add('border-emerald-900', 'bg-emerald-50/30');
                optUnframed.classList.remove('border-stone-200');
                unframedCircle?.classList.add('bg-emerald-700');
                unframedCircle?.classList.remove('bg-white', 'border-2', 'border-stone-300');
                unframedCheck?.classList.remove('hidden');

                optFramed.classList.remove('border-emerald-900', 'bg-emerald-50/30');
                optFramed.classList.add('border-stone-200');
                framedCircle?.classList.remove('bg-emerald-700');
                framedCircle?.classList.add('bg-white', 'border-2', 'border-stone-300');
                framedCheck?.classList.add('hidden');
            }
        }

        // Update frame preview visual
        if (shopFrame) {
            if (isFramed) {
                shopFrame.style.borderWidth = '16px';
                shopFrame.classList.add('border-stone-900');
                shopFrame.classList.remove('border-white', 'border-stone-200');
            } else {
                shopFrame.style.borderWidth = '8px';
                shopFrame.classList.remove('border-stone-900');
                shopFrame.classList.add('border-white');
            }
        }

        calculatePrice();
    };

    // Open inline letter selector panel
    const openLetterSelector = (index, letter) => {
        if (!altLetterContainer || !altLetterGrid || !currentLetterSpan) return;

        currentLetterSpan.textContent = letter;
        altLetterContainer.classList.remove('hidden');

        // Lock body scroll on mobile/tablet to prevent background scrolling
        // (matches the .alt-letter-panel full-screen overlay breakpoint, <=1023px)
        if (window.innerWidth < 1024) {
            document.body.style.overflow = 'hidden';
        }

        // Clear previous options
        altLetterGrid.innerHTML = '';

        const currentImageUrl = currentDesignImages[`${letter}_${index}`] || getLetterImage(letter.toLowerCase());

        probeVariations(letter).then(validUrls => {
            validUrls.forEach((variationUrl, vIndex) => {
                const option = document.createElement('div');
                const isCurrent = variationUrl === currentImageUrl;
                option.className = `rounded shadow-md cursor-pointer transition hover:scale-105 relative ${isCurrent ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`;

                // Explicit styles for visibility
                option.style.width = '100px';
                option.style.height = '130px';
                option.style.minWidth = '100px';
                option.style.minHeight = '130px';
                option.style.backgroundColor = '#1c1917';
                option.style.backgroundImage = `url('${variationUrl}')`;
                option.style.backgroundSize = 'cover';
                option.style.backgroundPosition = 'center';
                option.style.display = 'block';
                option.title = `Variation ${vIndex + 1}`;
                option.setAttribute('role', 'button');
                option.setAttribute('tabindex', '0');
                option.setAttribute('aria-label', `Letter ${letter}, variation ${vIndex + 1}${isCurrent ? ' (selected)' : ''}`);

                option.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        option.click();
                    }
                });

                option.addEventListener('click', () => {
                    updateLetterImageAtIndex(letter, index, variationUrl);
                    // Persist manual selection so it survives keystroke regenerations
                    assignedVariants[`${letter}_${index}`] = variationUrl;
                    // Track letter variation selection in GA4
                    trackLetterVariation(letter, index, vIndex + 1);
                    // Update selection ring
                    altLetterGrid.querySelectorAll('div').forEach(opt => {
                        opt.classList.remove('ring-2', 'ring-emerald-500', 'ring-offset-2');
                    });
                    option.classList.add('ring-2', 'ring-emerald-500', 'ring-offset-2');
                });

                altLetterGrid.appendChild(option);
            });
        });
    };

    // Close inline panel
    const closeAltPanel = () => {
        if (altLetterContainer) {
            altLetterContainer.classList.add('hidden');
            document.body.style.overflow = '';
        }
    };

    // Update letter image at specific index
    const updateLetterImageAtIndex = (letter, letterIndex, newImageUrl) => {
        const letterKey = `${letter}_${letterIndex}`;
        currentDesignImages[letterKey] = newImageUrl;
        currentDesignImages[letter] = newImageUrl;

        if (currentLetterDivs[letterIndex]) {
            const targetDiv = currentLetterDivs[letterIndex];
            targetDiv.style.backgroundImage = `url('${newImageUrl}')`;
        }
    };

    // Smart shuffle: randomize variants with duplicate differentiation
    // For names like "BARBARA", each A and each B gets a different variant.
    // Uses assignedVariants cache so variants persist across keystrokes.
    // forceAll=true (Shuffle button) clears cache and fully re-randomizes.
    const smartShuffle = (forceAll = false) => {
        const name = nameInput.value.trim().toUpperCase();
        if (!name) return;

        const letters = name.split('').filter(c => c.match(/[A-Z0-9]/));

        if (forceAll) {
            assignedVariants = {};
            trackShuffle(letters.length);
        }

        // Separate positions into already-assigned vs needs-assignment
        const needsAssignment = [];
        letters.forEach((letter, index) => {
            const key = `${letter}_${index}`;
            if (assignedVariants[key]) {
                // Re-apply persistent variant (needed after generateName resets DOM)
                updateLetterImageAtIndex(letter, index, assignedVariants[key]);
            } else {
                needsAssignment.push({ letter, index });
            }
        });

        // Clean up stale entries for positions that no longer exist
        const validKeys = new Set(letters.map((l, i) => `${l}_${i}`));
        Object.keys(assignedVariants).forEach(key => {
            if (!validKeys.has(key)) delete assignedVariants[key];
        });

        if (needsAssignment.length === 0) return;

        // Group new positions by character
        const groups = {};
        needsAssignment.forEach(({ letter, index }) => {
            if (!groups[letter]) groups[letter] = [];
            groups[letter].push(index);
        });

        // Collect already-used variants per character (to avoid duplicates)
        const usedByChar = {};
        letters.forEach((letter, index) => {
            const key = `${letter}_${index}`;
            if (assignedVariants[key]) {
                if (!usedByChar[letter]) usedByChar[letter] = [];
                usedByChar[letter].push(assignedVariants[key]);
            }
        });

        // Probe unique characters in parallel
        const uniqueChars = Object.keys(groups);
        Promise.all(uniqueChars.map(ch => probeVariations(ch))).then(allValid => {
            uniqueChars.forEach((char, i) => {
                const valid = allValid[i];
                if (valid.length === 0) return;

                const positions = groups[char];
                const alreadyUsed = usedByChar[char] || [];

                // Prefer variants not already used by this character
                let available = valid.filter(url => !alreadyUsed.includes(url));
                if (available.length === 0) available = [...valid];

                // Shuffle for randomness
                const shuffled = available.sort(() => Math.random() - 0.5);

                // Assign round-robin so duplicates get different variants
                positions.forEach((pos, posIdx) => {
                    const pick = shuffled[posIdx % shuffled.length];
                    updateLetterImageAtIndex(char, pos, pick);
                    assignedVariants[`${char}_${pos}`] = pick;
                });
            });
        });
    };

    // Generate name preview
    const generateName = () => {
        const name = nameInput.value.trim();
        if (!name) return;

        nameDisplay.innerHTML = '';
        currentDesignImages = {};
        currentLetterDivs = [];

        const letters = name.split('');
        let validName = false;
        let letterPosition = 0;

        letters.forEach((char, index) => {
            if (char.match(/[a-z0-9]/i)) {
                validName = true;
                const letterDiv = document.createElement('div');
                letterDiv.className = 'butterfly-letter fade-in cursor-pointer';
                // Inline styles guarantee no-scroll + no-matte sizing
                letterDiv.style.cssText = 'flex:0 1 100px;aspect-ratio:3/4;min-width:0;max-width:100px;overflow:hidden;position:relative;';
                letterDiv.style.animationDelay = `${index * CONFIG.ANIMATION_DELAY_MS}ms`;
                letterDiv.setAttribute('role', 'img');
                letterDiv.setAttribute('aria-label', `Letter ${char.toUpperCase()}, pattern ${letterPosition + 1}`);

                const imageUrl = getLetterImage(char);
                const letterKey = `${char.toUpperCase()}_${letterPosition}`;
                currentDesignImages[letterKey] = imageUrl;

                // Always set a background color first as fallback
                letterDiv.style.backgroundColor = '#1c1917';

                if (imageUrl && !imageUrl.includes('placeholder')) {
                    letterDiv.style.backgroundImage = `url('${imageUrl}')`;
                    letterDiv.style.backgroundSize = 'cover';
                    letterDiv.style.backgroundPosition = 'center';
                } else {
                    letterDiv.style.background = 'linear-gradient(135deg, #065f46 0%, #047857 100%)';
                    letterDiv.innerHTML = `<span class="text-white font-bold text-2xl sm:text-3xl">${char.toUpperCase()}</span>`;
                }

                // Click to open letter selector modal
                const currentLetter = char.toUpperCase();
                const currentIndex = letterPosition;
                letterDiv.addEventListener('click', () => {
                    openLetterSelector(currentIndex, currentLetter);
                });

                currentLetterDivs.push(letterDiv);
                nameDisplay.appendChild(letterDiv);
                letterPosition++;
            }
        });

        // Get signature watermark element
        const signatureWatermark = document.getElementById('signatureWatermark');

        if (validName) {
            // Show UI elements
            if (clickInstruction) clickInstruction.classList.remove('hidden');
            if (previewNotice) previewNotice.classList.remove('hidden');
            // Show signature watermark over the letters
            if (signatureWatermark) signatureWatermark.classList.remove('hidden');
            if (shuffleBtn) shuffleBtn.classList.remove('hidden');
            if (addToCartBtn) addToCartBtn.classList.remove('hidden');
            if (shareDesignBtn) shareDesignBtn.classList.toggle('hidden', !shareUnlocked);
            if (mobileCta) {
                mobileCta.classList.remove('hidden');
                document.body.classList.add('nb-cta-open');
            }

            calculatePrice();

            // Mobile horizontal-scroll affordance: show a right-edge fade when the
            // letter row overflows its container (long names on narrow viewports).
            const scrollFade = document.getElementById('nbScrollFade');
            if (scrollFade) scrollFade.classList.toggle('hidden', nameDisplay.scrollWidth <= nameDisplay.clientWidth + 2);

            // Analytics (skipped for the pre-filled sample name: the visitor did nothing)
            const letterCount = name.replace(/[^a-z0-9]/gi, '').length;
            if (!isSampleRender && window.gtag) {
                gtag('event', 'name_generated', {
                    'event_category': 'Name Builder',
                    'name_length': letterCount
                });
            }
            if (!isSampleRender && window.fbq) {
                fbq('track', 'CustomizeProduct', {
                    content_name: 'Butterfly Name',
                    content_category: 'Name Builder',
                    num_letters: letterCount
                });
            }

            // Auto-shuffle: randomize variants with duplicate differentiation
            // Distributes inventory usage and ensures duplicate letters look different
            // Skipped when loading specific variations from a shared URL
            if (!skipAutoShuffle) {
                smartShuffle();
            }
        } else {
            nameDisplay.innerHTML = '<span class="text-stone-500 italic serif text-xl">Enter a name to begin...</span>';
            if (clickInstruction) clickInstruction.classList.add('hidden');
            if (previewNotice) previewNotice.classList.add('hidden');
            if (shuffleBtn) shuffleBtn.classList.add('hidden');
            if (addToCartBtn) addToCartBtn.classList.add('hidden');
            if (shareDesignBtn) shareDesignBtn.classList.add('hidden');
            if (mobileCta) {
                mobileCta.classList.add('hidden');
                document.body.classList.remove('nb-cta-open');
            }
            const scrollFadeOff = document.getElementById('nbScrollFade');
            if (scrollFadeOff) scrollFadeOff.classList.add('hidden');
            // Hide signature watermark when no name
            if (signatureWatermark) signatureWatermark.classList.add('hidden');
        }
    };

    // Event listeners
    if (generateBtn) generateBtn.addEventListener('click', generateName);

    if (nameInput) {
        nameInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') generateName();
        });

        // Also generate on input for real-time preview
        nameInput.addEventListener('input', () => {
            shareUnlocked = true;

            // Track first interaction with name builder (GA4)
            if (!nameBuilderStarted && nameInput.value.length === 1) {
                nameBuilderStarted = true;
                trackGA4Event('name_builder_start', {
                    event_category: 'Name Builder',
                    page_section: 'name_builder'
                });
            }

            // Debounce - only generate after user stops typing
            clearTimeout(nameInput._debounceTimer);
            nameInput._debounceTimer = setTimeout(() => {
                if (nameInput.value.trim().length > 0) {
                    generateName();
                }
            }, 300);
        });
    }

    // Framing option changes
    framingOptions.forEach(radio => {
        radio.addEventListener('change', () => {
            updateFramingStyles();
            // Track framing selection in GA4
            trackFramingSelected(radio.value === 'framed');
        });
    });

    // Click handlers for framing option labels
    if (optFramed) {
        optFramed.addEventListener('click', () => {
            const radio = optFramed.querySelector('input[type="radio"]');
            if (radio) {
                radio.checked = true;
                updateFramingStyles();
            }
        });
    }

    if (optUnframed) {
        optUnframed.addEventListener('click', () => {
            const radio = optUnframed.querySelector('input[type="radio"]');
            if (radio) {
                radio.checked = true;
                updateFramingStyles();
            }
        });
    }

    // Google Shopping and the Shop app link to a specific variant (?variant=<id>). Open on
    // that framing so the landing price matches the listing the shopper clicked.
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant && String(urlVariant) === String(window.butterflyUnframedVariantId)) {
        const unframedRadio = document.querySelector('input[name="framing"][value="unframed"]');
        if (unframedRadio) unframedRadio.checked = true;
    }

    // Initialize framing styles
    updateFramingStyles();

    // Sticky mobile add-to-cart bar forwards to the real button so all
    // variant-selection, /cart/add.js and analytics logic is reused (not duplicated).
    if (mobileAddToCartBtn) {
        mobileAddToCartBtn.addEventListener('click', () => addToCartBtn?.click());
    }

    // Shuffle button
    if (shuffleBtn) {
        shuffleBtn.addEventListener('click', () => smartShuffle(true));
    }

    // Inline panel close handler
    if (closeAltPanelBtn) {
        closeAltPanelBtn.addEventListener('click', closeAltPanel);
    }

    // Backdrop click-to-close for mobile overlay
    const altLetterBackdrop = document.getElementById('altLetterBackdrop');
    if (altLetterBackdrop) {
        altLetterBackdrop.addEventListener('click', closeAltPanel);
    }

    // ========================================
    // SHARE DESIGN FUNCTIONALITY
    // ========================================

    // Share Design Modal Elements
    const shareDesignModal = document.getElementById('shareDesignModal');
    const shareModalBackdrop = document.getElementById('shareModalBackdrop');
    const closeShareModalBtn = document.getElementById('closeShareModalBtn');
    const shareModalPreview = document.getElementById('shareModalPreview');
    const shareStep1 = document.getElementById('shareStep1');
    const shareStep2 = document.getElementById('shareStep2');
    const shareEmailForm = document.getElementById('shareEmailForm');
    const shareEmailInput = document.getElementById('shareEmail');
    const shareEmailError = document.getElementById('shareEmailError');
    const shareDesignNameField = document.getElementById('shareDesignNameField');
    const shareUrlField = document.getElementById('shareUrlField');
    const shareUrlDisplay = document.getElementById('shareUrlDisplay');
    const copyShareLinkBtn = document.getElementById('copyShareLinkBtn');
    const copySuccessMsg = document.getElementById('copySuccessMsg');
    const shareSmsBtn = document.getElementById('shareSmsBtn');
    const shareEmailLinkBtn = document.getElementById('shareEmailLinkBtn');
    const sharePinterestBtn = document.getElementById('sharePinterestBtn');
    const shareDoneBtn = document.getElementById('shareDoneBtn');
    const shareEmailFailureBanner = document.getElementById('shareEmailFailureBanner');
    const shareEmailSuccessBlock = document.getElementById('shareEmailSuccessBlock');
    const shareRetryEmailBtn = document.getElementById('shareRetryEmailBtn');
    const submitShareEmailBtn = document.getElementById('submitShareEmail');

    // Initial markup for the submit button — kept so we can restore after the "Sending..." spinner
    const submitShareEmailBtnInitialHtml = submitShareEmailBtn ? submitShareEmailBtn.innerHTML : '';

    // Generate variation string for URL (e.g., "0,2,1,0,3" — variant index per letter)
    const generateVariationString = () => {
        const name = nameInput.value.trim().toUpperCase();
        const letters = name.split('').filter(c => c.match(/[A-Z0-9]/));

        return letters.map((letter, index) => {
            const imageUrl = currentDesignImages[`${letter}_${index}`] || currentDesignImages[letter];
            if (!imageUrl) return '0';
            // Extract variant index from URL (letter-a1.jpg → 1, letter-a2.jpg → 2, etc.)
            const match = imageUrl.match(/letter-[a-z0-9](\d+)\.jpg/);
            return match ? match[1] : '0';
        }).join(',');
    };

    // Generate shareable URL with design encoded in params
    const generateShareUrl = () => {
        const name = nameInput.value.trim().toUpperCase();
        if (!name) return null;

        // Validate: A-Z, 0-9 only, max length
        const safeName = name.replace(/[^A-Z0-9]/g, '').slice(0, CONFIG.MAX_NAME_LENGTH);
        if (!safeName) return null;

        const variations = generateVariationString();
        const isFramed = document.querySelector('input[name="framing"]:checked')?.value === 'framed';

        const params = new URLSearchParams();
        params.set('n', safeName);
        if (variations.replace(/0,?/g, '') !== '') params.set('v', variations); // Only include if non-default
        params.set('f', isFramed ? '1' : '0');

        return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    };

    // Load design from URL parameters
    const loadDesignFromUrl = () => {
        const params = new URLSearchParams(window.location.search);
        const rawName = params.get('n');
        const rawVariations = params.get('v') || '';
        const isFramed = params.get('f') === '1';

        if (!rawName) return false;

        // Sanitize name: A-Z, 0-9 only, enforce max length
        const name = rawName.replace(/[^A-Za-z0-9]/g, '').slice(0, CONFIG.MAX_NAME_LENGTH);
        if (!name) return false;

        // Bounds-check variation indices
        let variations = rawVariations;
        if (rawVariations) {
            const varArray = rawVariations.includes(',')
                ? rawVariations.split(',')
                : rawVariations.split('');
            variations = varArray.map(v => {
                const idx = parseInt(v, 10);
                return (isNaN(idx) || idx < 0 || idx > CONFIG.MAX_LETTER_VARIATIONS) ? '0' : String(idx);
            }).join(',');
        }

        // Skip auto-shuffle if URL has explicit variations
        if (variations) {
            skipAutoShuffle = true;
        }

        // Set the name
        shareUnlocked = true;
        nameInput.value = name;

        // Set framing option
        const framingRadio = document.querySelector(`input[name="framing"][value="${isFramed ? 'framed' : 'unframed'}"]`);
        if (framingRadio) framingRadio.checked = true;
        updateFramingStyles();

        // Generate the name first
        generateName();

        // Apply variations after a short delay to ensure images are loaded
        if (variations) {
            setTimeout(() => {
                const letters = name.split('').filter(c => c.match(/[A-Z0-9]/i));
                // Support both comma-separated ("0,2,1") and legacy binary ("0101") formats
                const varArray = variations.includes(',')
                    ? variations.split(',')
                    : variations.split('');
                letters.forEach((letter, index) => {
                    const varIndex = parseInt(varArray[index] || '0', 10);
                    const variationUrl = varIndex > 0
                        ? window.butterflyAssetUrlPattern.replace('letter-CHAR', `letter-${letter.toLowerCase()}${varIndex}`)
                        : window.butterflyAssetUrlPattern.replace('letter-CHAR', `letter-${letter.toLowerCase()}`);
                    updateLetterImageAtIndex(letter, index, variationUrl);
                    // Persist URL-loaded variants so they survive subsequent keystrokes
                    assignedVariants[`${letter}_${index}`] = variationUrl;
                });
                skipAutoShuffle = false; // Re-enable for subsequent user input
            }, 200);
        }

        return true;
    };

    // Track share events in GA4 (name field intentionally omitted — PII per GA4 ToS)
    const trackShareEmailSubmitted = (name, acceptsMarketing) => {
        trackGA4Event('share_email_submitted', {
            event_category: 'Name Builder',
            name_length: name.replace(/[^A-Z0-9]/gi, '').length,
            accepts_marketing: acceptsMarketing
        });
    };

    const trackDesignShared = (method, name) => {
        trackGA4Event('design_shared', {
            event_category: 'Name Builder',
            share_method: method,
            name_length: name.replace(/[^A-Z0-9]/gi, '').length
        });
    };

    // Send to Shopify Customers via hidden Liquid form
    const sendToShopify = async (email, name, acceptsMarketing) => {
        const hiddenForm = document.getElementById('ShareDesignCustomerForm');
        if (!hiddenForm) return false;

        const token = hiddenForm.querySelector('input[name="authenticity_token"]')?.value;
        if (!token) return false;

        const formData = new FormData();
        formData.append('form_type', 'customer');
        formData.append('utf8', '\u2713');
        formData.append('authenticity_token', token);
        formData.append('contact[email]', email);
        // Shopify drops storefront contact[tags] \u2014 Flask webhook handles tag attribution.
        if (acceptsMarketing) formData.append('contact[accepts_marketing]', 'true');

        try {
            await fetch('/contact', { method: 'POST', body: formData, redirect: 'manual' });
            return true;
        } catch (e) {
            return false;
        }
    };

    // Returns { ok, reason?, sendy? } — caller routes telemetry and UI on this.
    const sendShareEmail = async (email, name, shareUrl, acceptsMarketing) => {
        const cfUrl = window.butterflyShareEmailUrl;
        if (!cfUrl) return { ok: false, reason: 'no_url' };

        const letters = name.split('').filter(c => c.match(/[A-Z0-9]/));
        const imageUrls = letters.map((letter, index) => {
            return currentDesignImages[`${letter}_${index}`] || getLetterImage(letter.toLowerCase());
        });

        let response;
        try {
            response = await fetch(cfUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email,
                    name: name,
                    share_url: shareUrl,
                    image_urls: imageUrls,
                    // The "Send me updates" box: the endpoint adds them to Sendy only when true
                    accepts_marketing: acceptsMarketing === true
                })
            });
        } catch (err) {
            console.warn('Share email fetch error:', err);
            return { ok: false, reason: 'fetch_threw' };
        }

        if (!response.ok) {
            return { ok: false, reason: `http_${response.status}` };
        }

        let result;
        try {
            result = await response.json();
        } catch (err) {
            return { ok: false, reason: 'json_parse_err' };
        }

        if (!result.success) {
            return { ok: false, reason: 'success_false' };
        }
        return { ok: true, sendy: result.sendy };
    };

    // Open share modal
    const openShareModal = () => {
        if (!shareDesignModal || !shareUnlocked) return;

        const name = nameInput.value.trim().toUpperCase();
        if (!name) {
            alert('Please enter a name first.');
            return;
        }

        // Reset modal to step 1
        if (shareStep1) shareStep1.classList.remove('hidden');
        if (shareStep2) shareStep2.classList.add('hidden');
        if (shareEmailInput) shareEmailInput.value = '';
        if (shareEmailError) shareEmailError.classList.add('hidden');
        if (copySuccessMsg) copySuccessMsg.classList.add('hidden');

        // Reset Step 2 state from any prior failed attempt
        if (shareEmailFailureBanner) {
            shareEmailFailureBanner.textContent = '';
            shareEmailFailureBanner.classList.add('hidden');
        }
        if (shareEmailSuccessBlock) shareEmailSuccessBlock.classList.remove('hidden');
        if (shareRetryEmailBtn) shareRetryEmailBtn.classList.add('hidden');

        // Reset submit button (in case prior session left it on the "Sending..." spinner)
        if (submitShareEmailBtn) {
            submitShareEmailBtn.disabled = false;
            submitShareEmailBtn.innerHTML = submitShareEmailBtnInitialHtml;
        }

        // Populate preview with letter thumbnails
        if (shareModalPreview) {
            const letters = name.split('').filter(c => c.match(/[A-Z0-9]/));
            shareModalPreview.innerHTML = letters.map((letter, index) => {
                const imageUrl = currentDesignImages[`${letter}_${index}`] || getLetterImage(letter.toLowerCase());
                return `<div class="w-8 h-10 rounded bg-cover bg-center" style="background-image:url('${imageUrl}');background-color:#1c1917;"></div>`;
            }).join('');
        }

        // Show modal
        shareDesignModal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';

        // Focus email input
        setTimeout(() => {
            if (shareEmailInput) shareEmailInput.focus();
        }, 100);
    };

    // Close share modal
    const closeShareModal = () => {
        if (shareDesignModal) {
            shareDesignModal.classList.add('hidden');
            document.body.style.overflow = '';
        }
    };

    // Handle email form submission (Step 1 → Step 2)
    const handleShareEmailSubmit = async (e) => {
        e.preventDefault();
        if (!shareUnlocked) return;

        const email = shareEmailInput?.value?.trim();
        if (!email) {
            if (shareEmailError) {
                shareEmailError.textContent = 'Please enter your email address.';
                shareEmailError.classList.remove('hidden');
            }
            return;
        }

        // Basic email validation — TLD must be 2+ alphabetic chars (rejects a@b.c, test@test, etc.)
        if (!email.match(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/)) {
            if (shareEmailError) {
                shareEmailError.textContent = "That doesn't look like a complete email address.";
                shareEmailError.classList.remove('hidden');
            }
            return;
        }

        const name = nameInput.value.trim().toUpperCase();
        const shareUrl = generateShareUrl();
        const acceptsMarketing = document.getElementById('shareMarketingConsent')?.checked || false;

        // Update hidden fields
        if (shareDesignNameField) shareDesignNameField.value = name;
        if (shareUrlField) shareUrlField.value = shareUrl;

        // Show loading state
        if (submitShareEmailBtn) {
            submitShareEmailBtn.disabled = true;
            submitShareEmailBtn.innerHTML = '<svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Sending...';
        }

        // Send the design email first. Only save the customer to Shopify if the
        // email delivered — prevents typo addresses from creating dead customer
        // records with accepts_marketing=true.
        const emailResult = await sendShareEmail(email, name, shareUrl, acceptsMarketing);
        if (emailResult.ok) {
            await sendToShopify(email, name, acceptsMarketing);
        }

        const nameLen = name.replace(/[^A-Z0-9]/gi, '').length;
        if (!emailResult.ok) {
            trackGA4Event('share_email_failed', {
                reason: emailResult.reason,
                name_length: nameLen,
            });
        } else if (emailResult.sendy && emailResult.sendy !== 'subscribed') {
            // Email sent but Sendy subscribe didn't complete — track separately so we can see the leak
            trackGA4Event('share_design_sendy_skipped', {
                sendy_status: emailResult.sendy,
                name_length: nameLen,
            });
        }

        // Track in GA4
        trackShareEmailSubmitted(name, acceptsMarketing);

        // Also save to localStorage as backup
        const designData = {
            name: name,
            images: currentDesignImages,
            isFramed: document.querySelector('input[name="framing"]:checked')?.value === 'framed',
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('butterflyDesign', JSON.stringify(designData));

        // Update share URL display
        if (shareUrlDisplay) shareUrlDisplay.value = shareUrl;

        // Update share buttons
        const encodedUrl = encodeURIComponent(shareUrl);
        const shareText = `Check out this custom butterfly name art I designed: "${name}"`;
        const encodedText = encodeURIComponent(shareText);

        if (shareSmsBtn) shareSmsBtn.href = `sms:?body=${encodedText}%20${encodedUrl}`;
        if (shareEmailLinkBtn) shareEmailLinkBtn.href = `mailto:?subject=${encodeURIComponent(`Custom Butterfly Name Art: ${name}`)}&body=${encodedText}%0A%0A${encodedUrl}`;
        if (sharePinterestBtn) sharePinterestBtn.href = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}`;

        // Toggle Step 2 messaging based on whether email actually delivered
        if (!emailResult.ok) {
            if (shareEmailFailureBanner) {
                shareEmailFailureBanner.textContent =
                    "We couldn't send to that email — please double-check the spelling. " +
                    "Your design is still saved here and you can copy the link below.";
                shareEmailFailureBanner.classList.remove('hidden');
            }
            if (shareEmailSuccessBlock) shareEmailSuccessBlock.classList.add('hidden');
            if (shareRetryEmailBtn) shareRetryEmailBtn.classList.remove('hidden');
        } else {
            if (shareEmailFailureBanner) shareEmailFailureBanner.classList.add('hidden');
            if (shareEmailSuccessBlock) shareEmailSuccessBlock.classList.remove('hidden');
            if (shareRetryEmailBtn) shareRetryEmailBtn.classList.add('hidden');
        }

        // Switch to Step 2
        if (shareStep1) shareStep1.classList.add('hidden');
        if (shareStep2) shareStep2.classList.remove('hidden');

        // Reset submit button
        if (submitShareEmailBtn) {
            submitShareEmailBtn.disabled = false;
            submitShareEmailBtn.innerHTML = submitShareEmailBtnInitialHtml;
        }
    };

    // Copy link to clipboard
    const copyShareLink = () => {
        const url = shareUrlDisplay?.value;
        if (!url) return;

        navigator.clipboard.writeText(url).then(() => {
            if (copySuccessMsg) {
                copySuccessMsg.classList.remove('hidden');
                setTimeout(() => copySuccessMsg.classList.add('hidden'), 3000);
            }
            trackDesignShared('copy_link', nameInput.value.trim().toUpperCase());
        }).catch(() => {
            // Fallback for older browsers
            shareUrlDisplay.select();
            document.execCommand('copy');
            if (copySuccessMsg) {
                copySuccessMsg.classList.remove('hidden');
                setTimeout(() => copySuccessMsg.classList.add('hidden'), 3000);
            }
        });
    };

    // Event listeners for share modal
    if (shareDesignBtn) {
        shareDesignBtn.addEventListener('click', openShareModal);
    }

    if (closeShareModalBtn) {
        closeShareModalBtn.addEventListener('click', closeShareModal);
    }

    if (shareModalBackdrop) {
        shareModalBackdrop.addEventListener('click', closeShareModal);
    }

    if (shareEmailForm) {
        shareEmailForm.addEventListener('submit', handleShareEmailSubmit);
    }

    // Retry button: take user back to Step 1 with the input cleared and focused
    if (shareRetryEmailBtn) {
        shareRetryEmailBtn.addEventListener('click', () => {
            if (shareEmailInput) shareEmailInput.value = '';
            if (shareEmailError) shareEmailError.classList.add('hidden');
            if (shareStep2) shareStep2.classList.add('hidden');
            if (shareStep1) shareStep1.classList.remove('hidden');
            setTimeout(() => {
                if (shareEmailInput) shareEmailInput.focus();
            }, 80);
        });
    }

    if (copyShareLinkBtn) {
        copyShareLinkBtn.addEventListener('click', copyShareLink);
    }

    if (shareDoneBtn) {
        shareDoneBtn.addEventListener('click', closeShareModal);
    }

    // Track share button clicks
    if (shareSmsBtn) {
        shareSmsBtn.addEventListener('click', () => {
            trackDesignShared('sms', nameInput.value.trim().toUpperCase());
        });
    }

    if (shareEmailLinkBtn) {
        shareEmailLinkBtn.addEventListener('click', () => {
            trackDesignShared('email', nameInput.value.trim().toUpperCase());
        });
    }

    if (sharePinterestBtn) {
        sharePinterestBtn.addEventListener('click', () => {
            trackDesignShared('pinterest', nameInput.value.trim().toUpperCase());
        });
    }

    // Add to cart button
    if (addToCartBtn) {
        addToCartBtn.addEventListener('click', async () => {
            const name = nameInput.value.trim();
            if (!name) {
                alert('Please enter a name first.');
                return;
            }

            const letterCount = name.replace(/[^a-z0-9]/gi, '').length;
            if (letterCount === 0) {
                alert('Please enter a valid name with letters or numbers.');
                return;
            }

            const selectedFraming = document.querySelector('input[name="framing"]:checked');
            const isFramed = selectedFraming && selectedFraming.value === 'framed';
            const totalPrice = isFramed ? CONFIG.FRAMED_PRICE : CONFIG.BASE_PRICE;

            // Get correct variant based on framing selection
            const productVariantId = isFramed
                ? (addToCartBtn.getAttribute('data-framed-variant-id') || window.butterflyFramedVariantId)
                : (addToCartBtn.getAttribute('data-unframed-variant-id') || window.butterflyUnframedVariantId);

            // Validate variant ID is numeric
            if (!productVariantId || !/^\d+$/.test(String(productVariantId))) {
                alert('Product configuration missing. Please contact support.');
                return;
            }

            addToCartBtn.disabled = true;
            addToCartBtn.innerHTML = '<svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Adding...';

            try {
                const cartData = {
                    id: productVariantId,
                    quantity: 1,
                    properties: {
                        'Custom Name': name.toUpperCase(),
                        'Letter Count': letterCount.toString(),
                        'Letters': formatLettersForOrder(),
                        'Framing': isFramed ? 'Classic Black Frame' : 'Unframed Print'
                    }
                };

                // Note: _Letter_Images removed — cart preview reconstructs from Letters field

                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(cartData)
                });

                const data = await response.json();

                if (data.status) {
                    throw new Error(data.description || 'Failed to add to cart');
                }

                // Success
                if (window.PUB_SUB_EVENTS && window.publish) {
                    window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                        source: 'name-builder',
                        cartData: data
                    });
                }

                // Analytics
                if (window.gtag) {
                    gtag('event', 'add_to_cart', {
                        'currency': 'USD',
                        'value': totalPrice,
                        'items': [{
                            'item_name': 'Custom Butterfly Name',
                            'item_category': 'Name Builder',
                            'price': totalPrice,
                            'quantity': 1
                        }]
                    });
                }
                if (window.fbq) {
                    fbq('track', 'AddToCart', {
                        value: totalPrice,
                        currency: 'USD',
                        content_name: 'Custom Butterfly Name',
                        content_type: 'product'
                    });
                }

                // Show success popup
                showCartSuccessPopup();

                // Reset button state after brief delay
                addToCartBtn.disabled = false;
                addToCartBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clip-rule="evenodd" /></svg> Add to Cart';

            } catch (error) {
                console.error('Error adding to cart:', error);
                alert('Unable to add item to cart. Please try again or contact us for assistance.');
                addToCartBtn.disabled = false;
                addToCartBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clip-rule="evenodd" /></svg> Add to Cart';
            }
        });
    }

    // Prevent right-click on name display (IP protection)
    if (nameDisplay) {
        nameDisplay.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            return false;
        });
    }

    // Prevent right-click on letter selector panel (IP protection)
    if (altLetterContainer) {
        altLetterContainer.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            return false;
        });
    }

    // Prevent drag on any images (IP protection fallback - letters use CSS background-image)
    document.addEventListener('dragstart', (e) => {
        if (e.target.tagName === 'IMG' && e.target.closest('#NameDisplayArea, #altLetterContainer')) {
            e.preventDefault();
            return false;
        }
    });

    // Pre-fill a sample name so a first-time visitor sees a finished piece, not an empty frame.
    // The first tap or click into the field selects it, so typing replaces it.
    const showSampleName = () => {
        if (!nameInput || !CONFIG.SAMPLE_NAME) return;
        nameInput.value = CONFIG.SAMPLE_NAME;
        isSampleRender = true;
        generateName();
        isSampleRender = false;
        const selectSample = () => {
            if (nameInput.value === CONFIG.SAMPLE_NAME) nameInput.select();
        };
        nameInput.addEventListener('focus', selectSample, { once: true });
    };

    // Load design from URL params first (shared link), then localStorage, then the sample name
    const urlHasDesign = loadDesignFromUrl();
    let savedDesignShown = false;

    if (!urlHasDesign) {
        // Load saved design from localStorage if no URL params
        const savedDesign = localStorage.getItem('butterflyDesign');
        if (savedDesign) {
            try {
                const design = JSON.parse(savedDesign);
                // Expire saved designs after 30 days
                const savedTime = new Date(design.timestamp).getTime();
                if (isNaN(savedTime) || Date.now() - savedTime > 30 * 24 * 60 * 60 * 1000) {
                    localStorage.removeItem('butterflyDesign');
                } else if (design.name) {
                    nameInput.value = String(design.name).replace(/[^A-Za-z0-9]/g, '').slice(0, CONFIG.MAX_NAME_LENGTH);
                    shareUnlocked = true;
                    if (design.images && typeof design.images === 'object') {
                        const safeImages = {};
                        for (const [key, url] of Object.entries(design.images)) {
                            if (isValidImageUrl(url)) safeImages[key] = url;
                        }
                        currentDesignImages = safeImages;
                    }
                    if (design.isFramed) {
                        const framedRadio = document.querySelector('input[name="framing"][value="framed"]');
                        if (framedRadio) framedRadio.checked = true;
                    } else {
                        const unframedRadio = document.querySelector('input[name="framing"][value="unframed"]');
                        if (unframedRadio) unframedRadio.checked = true;
                    }
                    updateFramingStyles();
                    // Auto-generate after a small delay
                    setTimeout(generateName, 100);
                    savedDesignShown = true;
                }
            } catch (e) {
                console.warn('Could not load saved design:', e);
            }
        }
    }

    if (!urlHasDesign && !savedDesignShown) showSampleName();
});

// CSS for animations - minimal, relies on name-builder.css for main styles
const style = document.createElement('style');
style.textContent = `
    .butterfly-letter {
        position: relative;
        overflow: hidden;
        background-color: #1c1917;
    }
`;
document.head.appendChild(style);
